const ASSET_SEARCH_ATTRIBUTE_FIELDS = ['name', 'number', 'id'] as const;

interface GeoJsonFeatureLike {
  readonly properties?: Record<string, unknown>;
  readonly attributes?: Record<string, unknown>;
}

interface LayerFieldLike {
  readonly name?: string;
}

export interface QueryableAssetLayer {
  load?: () => Promise<void>;
  fields?: ReadonlyArray<LayerFieldLike>;
  queryFeatures?: (query: {
    where: string;
    outFields: string[];
    returnGeometry: boolean;
  }) => Promise<{
    features?: ReadonlyArray<{
      geometry?: unknown;
      attributes?: Record<string, unknown>;
    }>;
  }>;
}

export interface MatchedLayerFeature {
  readonly geometry?: unknown;
  readonly attributes: Record<string, unknown>;
  readonly graphic?: unknown;
}

export function escapeForWhere(value: string): string {
  return value.replace(/'/g, "''");
}

export function isAssetsLayer(
  key: string,
  explicitCategory?: string,
  checkedNodes?: ReadonlyArray<{ readonly key?: string; readonly category?: string }>,
): boolean {
  const category =
    explicitCategory ??
    checkedNodes?.find((node) => node?.key === key)?.category;

  if (category === 'Assets' || category === 'asset') {
    return true;
  }

  return (key || '').split('+')[0] === 'Assets';
}

export function getAssetTypeCodeFromLayerKey(layerKey: string): string | null {
  const parts = layerKey.split('+');
  if (parts[0] !== 'Assets' || !parts[1] || parts[1] === 'null') {
    return null;
  }
  return parts[1];
}

function readAttributeString(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim() !== '') {
    return value;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return undefined;
}

function getAssetSearchFieldPriority(
  keyword: string,
): ReadonlyArray<(typeof ASSET_SEARCH_ATTRIBUTE_FIELDS)[number]> {
  return /^\d+$/.test(keyword.trim())
    ? ['id', 'number', 'name']
    : ['name', 'number', 'id'];
}

export function getAssetSearchMatchField(
  attributes: Record<string, unknown>,
  keyword: string,
): (typeof ASSET_SEARCH_ATTRIBUTE_FIELDS)[number] | null {
  const trimmed = keyword.trim();
  const normalizedKeyword = trimmed.toLowerCase();
  if (!normalizedKeyword) {
    return null;
  }

  const fieldPriority = getAssetSearchFieldPriority(trimmed);

  for (const field of fieldPriority) {
    const value = readAttributeString(attributes[field]);
    if (value?.toLowerCase() === normalizedKeyword) {
      return field;
    }
  }

  for (const field of fieldPriority) {
    const value = readAttributeString(attributes[field]);
    if (value?.toLowerCase().includes(normalizedKeyword)) {
      return field;
    }
  }

  return null;
}

export function getAssetSearchResultLabel(
  attributes: Record<string, unknown>,
  keyword: string,
): string | null {
  const matchedField = getAssetSearchMatchField(attributes, keyword);
  if (matchedField) {
    return readAttributeString(attributes[matchedField]) ?? null;
  }

  return getAssetFeatureDisplayLabel(attributes);
}

export function getAssetFeatureDisplayLabel(
  attributes: Record<string, unknown>,
): string | null {
  const name = readAttributeString(attributes.name);
  if (name) {
    return name;
  }

  const number = readAttributeString(attributes.number);
  if (number) {
    return number;
  }

  const id = readAttributeString(attributes.id);
  return id ?? null;
}

function parseExactNumericId(keyword: string): number | null {
  const trimmed = keyword.trim();
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }

  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return Math.trunc(parsed);
}

export function buildAssetSearchWhereClause(
  keyword: string,
  availableFields: readonly string[] = [...ASSET_SEARCH_ATTRIBUTE_FIELDS],
): string {
  const trimmed = keyword.trim();
  if (!trimmed) {
    return '1=0';
  }

  const escapedKeyword = escapeForWhere(trimmed);
  const fieldSet = new Set(availableFields);
  const clauses: string[] = [];

  if (fieldSet.has('name')) {
    clauses.push(`name LIKE '%${escapedKeyword}%'`);
  }
  if (fieldSet.has('number')) {
    clauses.push(`number LIKE '%${escapedKeyword}%'`);
  }
  if (fieldSet.has('id')) {
    const numericId = parseExactNumericId(trimmed);
    if (numericId !== null) {
      clauses.push(`id = ${numericId}`);
    }
  }

  return clauses.length > 0 ? clauses.join(' OR ') : '1=1';
}

export function buildAssetSelectWhereClause(
  selectedItem: string,
  availableFields: readonly string[] = [...ASSET_SEARCH_ATTRIBUTE_FIELDS],
): string {
  const trimmed = selectedItem.trim();
  if (!trimmed) {
    return '1=0';
  }

  const escaped = escapeForWhere(trimmed);
  const fieldSet = new Set(availableFields);
  const clauses: string[] = [];

  if (fieldSet.has('name')) {
    clauses.push(`name = '${escaped}'`);
  }
  if (fieldSet.has('number')) {
    clauses.push(`number = '${escaped}'`);
  }
  if (fieldSet.has('id')) {
    const numericId = parseExactNumericId(trimmed);
    if (numericId !== null) {
      clauses.push(`id = ${numericId}`);
    }
  }

  return clauses.length > 0 ? `(${clauses.join(' OR ')})` : '1=0';
}

export function buildAssetHighlightWhereClause(
  items: readonly string[],
  availableFields: readonly string[] = [...ASSET_SEARCH_ATTRIBUTE_FIELDS],
): string {
  if (items.length === 0) {
    return '1=0';
  }

  return items
    .map((item) => buildAssetSelectWhereClause(item, availableFields))
    .join(' OR ');
}

export function assetFeatureMatchesKeyword(
  attributes: Record<string, unknown>,
  keyword: string,
): boolean {
  const normalizedKeyword = keyword.trim().toLowerCase();
  if (!normalizedKeyword) {
    return false;
  }

  for (const field of ASSET_SEARCH_ATTRIBUTE_FIELDS) {
    const value = readAttributeString(attributes[field]);
    if (value?.toLowerCase().includes(normalizedKeyword)) {
      return true;
    }
  }

  return false;
}

export function assetFeatureMatchesDisplayLabel(
  attributes: Record<string, unknown>,
  label: string,
): boolean {
  const normalizedLabel = label.trim().toLowerCase();
  if (!normalizedLabel) {
    return false;
  }

  const displayLabel = getAssetFeatureDisplayLabel(attributes);
  if (displayLabel?.toLowerCase() === normalizedLabel) {
    return true;
  }

  for (const field of ASSET_SEARCH_ATTRIBUTE_FIELDS) {
    const value = readAttributeString(attributes[field]);
    if (value?.toLowerCase() === normalizedLabel) {
      return true;
    }
  }

  return false;
}

function collectLabelsFromFeatures(
  features: ReadonlyArray<{ attributes?: Record<string, unknown> }>,
  keyword: string,
): string[] {
  const labels = new Set<string>();
  for (const feature of features) {
    const attributes = feature.attributes ?? {};
    if (!assetFeatureMatchesKeyword(attributes, keyword)) {
      continue;
    }
    const label = getAssetSearchResultLabel(attributes, keyword);
    if (label) {
      labels.add(label);
    }
  }
  return [...labels];
}

async function queryLayerFeatures(
  layer: QueryableAssetLayer,
  where: string,
  returnGeometry: boolean,
): Promise<ReadonlyArray<MatchedLayerFeature>> {
  if (!layer.queryFeatures) {
    return [];
  }

  if (layer.load) {
    await layer.load();
  }

  const results = await layer.queryFeatures({
    where,
    outFields: ['*'],
    returnGeometry,
  });

  return (results.features ?? []).map((feature) => ({
    geometry: feature.geometry,
    attributes: feature.attributes ?? {},
    graphic: feature,
  }));
}

export function getHighlightGraphics(
  features: ReadonlyArray<MatchedLayerFeature>,
): unknown[] {
  return features
    .map((feature) => feature.graphic)
    .filter((graphic): graphic is unknown => graphic != null);
}

export async function queryMatchingLayerFeatures(
  layer: QueryableAssetLayer,
  matcher: (attributes: Record<string, unknown>) => boolean,
  returnGeometry = false,
): Promise<readonly MatchedLayerFeature[]> {
  try {
    const features = await queryLayerFeatures(layer, '1=1', returnGeometry);
    return features.filter((feature) => matcher(feature.attributes));
  } catch {
    return [];
  }
}

export async function findLayerFeaturesByDisplayLabels(
  layer: QueryableAssetLayer,
  labels: readonly string[],
  returnGeometry = false,
): Promise<readonly MatchedLayerFeature[]> {
  if (labels.length === 0) {
    return [];
  }

  return queryMatchingLayerFeatures(
    layer,
    (attributes) =>
      labels.some((label) => assetFeatureMatchesDisplayLabel(attributes, label)),
    returnGeometry,
  );
}

export async function findLayerFeatureByDisplayLabel(
  layer: QueryableAssetLayer,
  label: string,
  returnGeometry = false,
): Promise<MatchedLayerFeature | null> {
  const matches = await findLayerFeaturesByDisplayLabels(layer, [label], returnGeometry);
  return matches[0] ?? null;
}

async function loadAllAssetSearchFeatures(options: {
  readonly layer?: QueryableAssetLayer | null;
  readonly fetchGeoJson?: () => Promise<{ features?: ReadonlyArray<GeoJsonFeatureLike> }>;
}): Promise<readonly MatchedLayerFeature[]> {
  if (options.layer?.queryFeatures) {
    try {
      const layerFeatures = await queryMatchingLayerFeatures(options.layer, () => true);
      if (layerFeatures.length > 0) {
        return layerFeatures;
      }
    } catch {
      // Fall back to GeoJSON fetch below.
    }
  }

  if (options.fetchGeoJson) {
    try {
      const geojson = await options.fetchGeoJson();
      return (geojson.features ?? []).map((feature) => ({
        attributes: feature.properties ?? feature.attributes ?? {},
      }));
    } catch {
      return [];
    }
  }

  return [];
}

export async function enrichAssetSearchResultLabels(options: {
  readonly keyword: string;
  readonly labels: readonly string[];
  readonly layer?: QueryableAssetLayer | null;
  readonly fetchGeoJson?: () => Promise<{ features?: ReadonlyArray<GeoJsonFeatureLike> }>;
}): Promise<string[]> {
  const keyword = options.keyword.trim();
  if (!keyword || options.labels.length === 0) {
    return [...options.labels];
  }

  const features = await loadAllAssetSearchFeatures(options);
  if (features.length === 0) {
    return [...options.labels];
  }

  const enrichedLabels = new Set<string>();
  for (const label of options.labels) {
    const matchedFeature =
      features.find((feature) =>
        assetFeatureMatchesDisplayLabel(feature.attributes, label),
      ) ??
      features.find((feature) =>
        assetFeatureMatchesKeyword(feature.attributes, keyword),
      );

    if (matchedFeature) {
      const resolvedLabel = getAssetSearchResultLabel(
        matchedFeature.attributes,
        keyword,
      );
      if (resolvedLabel) {
        enrichedLabels.add(resolvedLabel);
        continue;
      }
    }

    enrichedLabels.add(label);
  }

  return [...enrichedLabels];
}

export async function searchAssetLayerLabels(options: {
  readonly keyword: string;
  readonly layer?: QueryableAssetLayer | null;
  readonly fetchGeoJson?: () => Promise<{ features?: ReadonlyArray<GeoJsonFeatureLike> }>;
}): Promise<string[]> {
  const keyword = options.keyword.trim();
  if (!keyword) {
    return [];
  }

  const layer = options.layer;
  if (layer?.queryFeatures) {
    try {
      const features = await queryMatchingLayerFeatures(
        layer,
        (attributes) => assetFeatureMatchesKeyword(attributes, keyword),
      );
      const labels = collectLabelsFromFeatures(features, keyword);
      if (labels.length > 0) {
        return labels;
      }
    } catch {
      // Fall back to GeoJSON fetch below.
    }
  }

  if (options.fetchGeoJson) {
    try {
      const geojson = await options.fetchGeoJson();
      const labels = new Set<string>();
      for (const feature of geojson.features ?? []) {
        const attributes = feature.properties ?? feature.attributes ?? {};
        if (!assetFeatureMatchesKeyword(attributes, keyword)) {
          continue;
        }
        const label = getAssetSearchResultLabel(attributes, keyword);
        if (label) {
          labels.add(label);
        }
      }
      return [...labels];
    } catch {
      return [];
    }
  }

  return [];
}

export async function searchCurrentLayerAssetLabels(options: {
  readonly keyword: string;
  readonly layerKey: string | null;
  readonly layer?: QueryableAssetLayer | null;
  readonly fetchGeoJson?: () => Promise<{ features?: ReadonlyArray<GeoJsonFeatureLike> }>;
  readonly searchAssets?: (
    keyword: string,
    assetTypeCode: string,
  ) => Promise<readonly string[]>;
}): Promise<string[]> {
  const keyword = options.keyword.trim();
  if (!keyword) {
    return [];
  }

  const layerResults = await searchAssetLayerLabels({
    keyword,
    layer: options.layer,
    fetchGeoJson: options.fetchGeoJson,
  });

  if (layerResults.length > 0) {
    return layerResults;
  }

  const assetTypeCode = options.layerKey
    ? getAssetTypeCodeFromLayerKey(options.layerKey)
    : null;
  if (!assetTypeCode || !options.searchAssets) {
    return [];
  }

  try {
    const apiResults = await options.searchAssets(keyword, assetTypeCode);
    return enrichAssetSearchResultLabels({
      keyword,
      labels: apiResults,
      layer: options.layer,
      fetchGeoJson: options.fetchGeoJson,
    });
  } catch {
    return [];
  }
}

export const assetLayerSearchFields = ASSET_SEARCH_ATTRIBUTE_FIELDS;
